"use client";

import { getIsQuickApplySchool } from "@nimble/candidate-api/quick-apply";
import { JobPosting, SchoolData } from "@nimble/candidate-api/types";
import { ReactNode } from "react";

import { ActionButtonVariant } from "@/components/shared/buttons/button-variants";
import ExpressInterestWaitlistContainer from "@/components/shared/buttons/express-interest-waitlist-container/express-interest-waitlist-container";
import ExternalApplicationLinkButton from "@/components/shared/buttons/external-application-link-button/external-application-link-button";
import QuickApplyButton from "@/components/shared/buttons/quick-apply-button/quick-apply-button";
import JobViewActionsView from "@/components/shared/jobs/job-view-actions/job-view-actions.view";
import { jobActionButtonKeys } from "@/lib/constants/jobs.constants";
import { jobViewActionsTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import { isHiringSeasonDormant } from "@/lib/dormant-season/resolve";
import { getIsOnConnect } from "@/lib/helpers/get-is-on-connect";
import { getNimbleAtsJobId } from "@/lib/helpers/get-nimble-ats-job-id";

/**
 * Determines the interest message to display based on which action buttons are available
 * @param showExpressInterest - Whether the express interest button will be shown
 * @param showQuickApply - Whether the quick apply button will be shown
 * @returns The appropriate message string
 */
export function getJobInterestMessage(
  showExpressInterest: boolean,
  showQuickApply: boolean,
): string {
  if (showExpressInterest) {
    // Express interest is available alongside another action button
    return "Connect with the principal about applying for this role";
  } else if (showQuickApply) {
    // Only quick apply
    return "Apply to this role easily using your Nimble profile";
  } else {
    // Only link to formal application
    return "Submit a formal application for this position";
  }
}

interface JobViewActionsContainerProps {
  jobPosting: JobPosting;
  schoolData: SchoolData;
  className?: string;
  titleId?: string;
}

export default function JobViewActionsContainer({
  jobPosting,
  schoolData,
  className,
  titleId,
}: JobViewActionsContainerProps) {
  const showExpressInterest = getIsOnConnect(schoolData);

  const jobUrl = jobPosting.job_url;
  const jobId = jobPosting.uuid;
  const nimbleAtsJobId = getNimbleAtsJobId(jobPosting);
  const schoolId = schoolData.nces_id;
  const showQuickApply =
    getIsQuickApplySchool(schoolData) && nimbleAtsJobId !== null;

  const interestMessage = getJobInterestMessage(
    showExpressInterest && !isHiringSeasonDormant("express-interest"),
    showQuickApply,
  );

  const buttons: ReactNode[] = [];

  if (showExpressInterest) {
    buttons.push(
      <ExpressInterestWaitlistContainer
        key={jobActionButtonKeys.EXPRESS_INTEREST}
        schoolId={schoolId}
        schoolData={schoolData}
        variant={ActionButtonVariant.PrimaryLg}
        testId={`${jobViewActionsTestIds.JOB_VIEW_ACTIONS_EXPRESS_INTEREST_BUTTON}-${schoolId}`}
      />,
    );
  }

  if (showQuickApply) {
    buttons.push(
      <QuickApplyButton
        key={jobActionButtonKeys.QUICK_APPLY}
        jobId={jobId}
        nimbleAtsJobId={nimbleAtsJobId}
        jobUrl={jobUrl}
      />,
    );
  } else {
    buttons.push(
      <ExternalApplicationLinkButton
        key={jobActionButtonKeys.EXTERNAL_APPLICATION}
        jobUrl={jobUrl}
        jobId={jobId}
      />,
    );
  }

  return (
    <JobViewActionsView
      className={className}
      interestMessage={interestMessage}
      buttons={buttons}
      titleId={titleId}
    />
  );
}
